import React, { useState } from 'react';
import { BoltIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

/**
 * `Globex Corp, S.L.` → `globexcorp`
 *
 * El paso por NFD **no es decorativo**: separa la tilde de su letra para que
 * `Peña` acabe en `pena` y no en `pea`, que es lo que saldría si se limpiara a
 * lo bruto. El rango va escrito con escapes Unicode y no con los caracteres
 * literales: son marcas combinantes, invisibles en el código, y sobreviven mal
 * a un cambio de codificación del fichero.
 */
const COMBINING_MARKS = /[\u0300-\u036f]/g;

const toDnsLabel = name =>
  String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 40);

const LABEL_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/**
 * Las dos vías para conectar el correo.
 *
 * **No se presentan como equivalentes a propósito.** La diferencia que le importa
 * al cliente no es técnica: en la gestionada el deudor ve nuestro dominio y la
 * reputación se comparte con otros clientes; en la suya el deudor ve su dominio
 * y la reputación es solo suya, a cambio de tocar el DNS. Ocultar eso detrás de
 * dos botones iguales sería hacerle elegir a ciegas algo que después no se puede
 * cambiar sin perder las respuestas en vuelo.
 */
const EmailSetupChoice = ({ companyName, managedDomain, managedAvailable, onSubmit, isSaving }) => {
  const [mode, setMode] = useState(null);
  const [displayName, setDisplayName] = useState(companyName || '');
  const [localPart, setLocalPart] = useState('facturacion');
  const [label, setLabel] = useState(toDnsLabel(companyName));
  const [domain, setDomain] = useState('');
  const [error, setError] = useState(null);

  const previewDomain =
    mode === 'managed' ? `${label || '…'}.${managedDomain || '…'}` : domain || '…';

  const submit = e => {
    e.preventDefault();
    setError(null);

    if (!displayName.trim()) return setError('Falta el nombre visible.');
    if (!LABEL_RE.test(localPart)) {
      return setError('La parte antes de la @ solo admite letras, números y guiones.');
    }
    if (mode === 'managed' && !LABEL_RE.test(label)) {
      return setError('La etiqueta solo admite letras, números y guiones.');
    }
    if (mode === 'own_domain' && !DOMAIN_RE.test(domain)) {
      return setError('Escribe un subdominio válido, sin https:// ni barras.');
    }

    onSubmit({
      mode,
      display_name: displayName.trim(),
      from_local_part: localPart,
      ...(mode === 'managed' ? { label } : { domain }),
    });
  };

  if (!mode) {
    return (
      <div className='space-y-4'>
        <p className='text-sm text-slate-600'>
          Para que tus deudores reciban los correos con <span className='font-medium'>tu
          identidad</span> y no con la de ClaimUp, hay que conectar un dominio de envío. Hay
          dos formas:
        </p>

        <div className='grid gap-3 md:grid-cols-2'>
          <button
            type='button'
            disabled={!managedAvailable}
            onClick={() => setMode('managed')}
            className={`text-left p-4 border rounded-lg ${
              managedAvailable
                ? 'border-slate-200 hover:border-blue-400 bg-white'
                : 'border-slate-200 bg-slate-50 cursor-not-allowed opacity-60'
            }`}
          >
            <div className='flex items-center gap-2 mb-2'>
              <BoltIcon className='w-5 h-5 text-blue-600' />
              <span className='font-medium text-slate-900'>Nos encargamos nosotros</span>
            </div>
            <p className='text-sm text-slate-600 mb-2'>
              Listo en unos minutos. <span className='font-medium'>No tienes que tocar nada
              técnico.</span>
            </p>
            <ul className='text-xs text-slate-500 space-y-1'>
              <li>· El deudor ve tu nombre, con un dominio nuestro</li>
              <li>· La reputación se comparte con otros clientes</li>
            </ul>
            {!managedAvailable && (
              <p className='text-xs text-amber-700 mt-2'>
                No disponible en este entorno todavía.
              </p>
            )}
          </button>

          <button
            type='button'
            onClick={() => setMode('own_domain')}
            className='text-left p-4 border border-slate-200 rounded-lg hover:border-blue-400 bg-white'
          >
            <div className='flex items-center gap-2 mb-2'>
              <GlobeAltIcon className='w-5 h-5 text-blue-600' />
              <span className='font-medium text-slate-900'>Con tu propio dominio</span>
            </div>
            <p className='text-sm text-slate-600 mb-2'>
              Hay que añadir <span className='font-medium'>cuatro registros</span> en tu DNS.
              Suele hacerlo quien lleva vuestra web.
            </p>
            <ul className='text-xs text-slate-500 space-y-1'>
              <li>· El deudor ve tu dominio real</li>
              <li>· La reputación es solo tuya</li>
            </ul>
          </button>
        </div>

        <p className='text-xs text-slate-500'>
          Usaremos siempre un <span className='font-medium'>subdominio</span>, nunca tu dominio
          principal: así tu correo de siempre no se ve afectado pase lo que pase.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className='space-y-4'>
      <button
        type='button'
        onClick={() => setMode(null)}
        className='text-sm text-blue-600 hover:text-blue-800'
      >
        ← Cambiar de opción
      </button>

      <div className='grid gap-3 md:grid-cols-2'>
        <div>
          <label htmlFor='email-display-name' className='block text-sm text-slate-600 mb-1'>
            Nombre visible
          </label>
          <input
            id='email-display-name'
            type='text'
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder='Globex Corp'
            className='w-full border border-slate-300 rounded-lg px-3 py-2 text-sm'
          />
        </div>

        <div>
          <label htmlFor='email-local-part' className='block text-sm text-slate-600 mb-1'>
            Antes de la @
          </label>
          <input
            id='email-local-part'
            type='text'
            value={localPart}
            onChange={e => setLocalPart(e.target.value.toLowerCase())}
            placeholder='facturacion'
            className='w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono'
          />
        </div>

        {mode === 'managed' ? (
          <div className='md:col-span-2'>
            <label htmlFor='email-label' className='block text-sm text-slate-600 mb-1'>
              Etiqueta del subdominio
            </label>
            <div className='flex items-center gap-2'>
              <input
                id='email-label'
                type='text'
                value={label}
                onChange={e => setLabel(e.target.value.toLowerCase())}
                className='flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono'
              />
              <span className='text-sm text-slate-500 font-mono'>.{managedDomain}</span>
            </div>
          </div>
        ) : (
          <div className='md:col-span-2'>
            <label htmlFor='email-domain' className='block text-sm text-slate-600 mb-1'>
              Subdominio tuyo
            </label>
            <input
              id='email-domain'
              type='text'
              value={domain}
              onChange={e => setDomain(e.target.value.toLowerCase().trim())}
              placeholder='facturacion.tuempresa.com'
              className='w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono'
            />
            <p className='text-xs text-slate-500 mt-1'>
              Un subdominio, no el dominio principal. Si tu web es{' '}
              <span className='font-mono'>tuempresa.com</span>, algo como{' '}
              <span className='font-mono'>facturacion.tuempresa.com</span>.
            </p>
          </div>
        )}
      </div>

      <div className='bg-slate-50 border border-slate-200 rounded-lg px-3 py-2'>
        <p className='text-xs text-slate-500 mb-1'>Tus deudores verán:</p>
        <p className='text-sm font-mono text-slate-800 break-all'>
          {displayName || '…'} &lt;{localPart || '…'}@{previewDomain}&gt;
        </p>
      </div>

      {error && <p className='text-sm text-red-600'>{error}</p>}

      <button
        type='submit'
        disabled={isSaving}
        className={`px-4 py-2 rounded-lg text-white text-sm font-medium ${
          isSaving ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
        }`}
      >
        {isSaving ? 'Conectando…' : 'Conectar correo'}
      </button>
    </form>
  );
};

export default EmailSetupChoice;
export { toDnsLabel };
